import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Minus, Download, VolumeX, Mail, X, Plus, Send, History, ChevronDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Section } from '../FormFields';
import { colorForLabel } from '../../../utils/colorHash';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);
const formatHour = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;
const NUDGE_COOLDOWN_HOURS = 12;
const timeAgo = (iso) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
const cooldownRemainingMs = (iso, now) => {
  if (!iso) return 0;
  const elapsedMs = now - new Date(iso).getTime();
  // Clamp to [0, cooldown] — a slightly-ahead server timestamp (clock skew,
  // or `now` captured just before the nudge response lands) would otherwise
  // render a brief ">12h" countdown right after clicking "Ping owner".
  return Math.min(NUDGE_COOLDOWN_HOURS * 3600000, Math.max(0, NUDGE_COOLDOWN_HOURS * 3600000 - elapsedMs));
};
const formatCountdown = (ms) => {
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const DeltaBadge = ({ delta }) => {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600">
        <TrendingUp className="w-3 h-3" /> +{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-500">
        <TrendingDown className="w-3 h-3" /> {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-slate-400">
      <Minus className="w-3 h-3" /> 0
    </span>
  );
};

const WeeklyInboundDigest = () => {
  const [rows, setRows] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [bySourcePrevious, setBySourcePrevious] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [savingCc, setSavingCc] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendWeekday, setSendWeekday] = useState(0);
  const [sendHour, setSendHour] = useState(8);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [nudgingRegion, setNudgingRegion] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [sendLog, setSendLog] = useState([]);
  const [expandedLogKey, setExpandedLogKey] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = () => {
    // Built on the same trend (default days=7) + previous_period_total +
    // by_source(_previous) data Region Health already fetches — no
    // separate endpoint needed for this rollup.
    axios.get(`${BACKEND_URL}/api/admin/region-settings/health`, { withCredentials: true })
      .then((res) => {
        const regions = res.data.regions || [];
        const withTotals = regions
          .map((r) => ({
            region: r.region,
            name: r.name,
            weekly_total: (r.trend || []).reduce((a, b) => a + b, 0),
            previous_total: r.previous_period_total || 0,
            went_quiet: (r.trend || []).reduce((a, b) => a + b, 0) === 0 && (r.previous_period_total || 0) > 0,
            owner_username: r.owner_username,
            owner_name: r.owner_name,
            last_nudged_at: r.last_nudged_at,
          }))
          .sort((a, b) => b.weekly_total - a.weekly_total);
        setRows(withTotals);
        setBySource(res.data.by_source || []);
        setBySourcePrevious(res.data.by_source_previous || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHealth(); }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/settings`, { withCredentials: true })
      .then((res) => {
        setCcEmails(res.data.cc_emails || []);
        setSendWeekday(res.data.send_weekday ?? 0);
        setSendHour(res.data.send_hour ?? 8);
      })
      .catch(() => {});
  }, []);

  const refreshPreviewIfOpen = () => {
    if (!previewOpen) return;
    axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/preview`, { withCredentials: true })
      .then((res) => setPreview(res.data))
      .catch(() => {});
  };

  const saveCcEmails = async (next) => {
    setSavingCc(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/settings`, { cc_emails: next }, { withCredentials: true });
      setCcEmails(res.data.cc_emails || []);
      refreshPreviewIfOpen();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update CC recipients');
    } finally {
      setSavingCc(false);
    }
  };

  const handleAddCc = () => {
    const email = ccInput.trim().toLowerCase();
    if (!email) return;
    if (!email.includes('@') || !email.includes('.')) {
      toast.error(`"${email}" doesn't look like a valid email`);
      return;
    }
    if (ccEmails.includes(email)) {
      toast(`${email} is already on the CC list`);
      setCcInput('');
      return;
    }
    saveCcEmails([...ccEmails, email]);
    setCcInput('');
  };

  const handleRemoveCc = (email) => saveCcEmails(ccEmails.filter((e) => e !== email));

  const handleScheduleChange = async (nextWeekday, nextHour) => {
    setSavingSchedule(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/region-settings/weekly-digest/settings`,
        { send_weekday: nextWeekday, send_hour: nextHour },
        { withCredentials: true }
      );
      setSendWeekday(res.data.send_weekday ?? 0);
      setSendHour(res.data.send_hour ?? 8);
      refreshPreviewIfOpen();
      toast.success(`Weekly digest will now send every ${WEEKDAY_LABELS[res.data.send_weekday ?? 0]} at ${formatHour(res.data.send_hour ?? 8)} (Manila time)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update the digest schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleNudgeOwner = async (region) => {
    setNudgingRegion(region);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/region-settings/${region}/nudge-owner`, {}, { withCredentials: true });
      if (res.data.suppressed) {
        toast(`Safe Test Mode suppressed the nudge to ${res.data.owner_email}`);
      } else if (res.data.sent) {
        toast.success(`Nudged ${res.data.owner_email}`);
      } else {
        toast.error('Failed to send the nudge email');
      }
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to nudge the region owner');
    } finally {
      setNudgingRegion(null);
    }
  };

  const handleToggleLog = async () => {
    if (logOpen) {
      setLogOpen(false);
      return;
    }
    setLoadingLog(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/log`, { withCredentials: true });
      setSendLog(res.data.log || []);
      setLogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load digest history');
    } finally {
      setLoadingLog(false);
    }
  };

  const handleTogglePreview = async () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setPreviewing(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/preview`, { withCredentials: true });
      setPreview(res.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load digest preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/weekly-digest/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `weekly-inbound-digest-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export the digest CSV');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return null;
  const grandTotal = rows.reduce((a, r) => a + r.weekly_total, 0);
  const grandPreviousTotal = rows.reduce((a, r) => a + r.previous_total, 0);
  if (grandTotal === 0 && grandPreviousTotal === 0) return null;
  const max = Math.max(...rows.map((r) => r.weekly_total), 1);
  const quietRegions = rows.filter((r) => r.went_quiet);

  const sourceNames = Array.from(new Set([...bySource.map((s) => s.source), ...bySourcePrevious.map((s) => s.source)]));
  const sourceChartData = sourceNames.map((source) => ({
    source,
    this_week: bySource.find((s) => s.source === source)?.count || 0,
    last_week: bySourcePrevious.find((s) => s.source === source)?.count || 0,
  }));

  return (
    <Section
      title="Weekly Inbound Digest"
      description="Inbound lead volume per region, this week vs. last week — a lightweight snapshot without opening the full Analytics dashboard."
    >
      <div data-testid="weekly-inbound-digest" className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600 flex items-center gap-1.5" data-testid="weekly-inbound-total">
            <TrendingUp className="w-4 h-4 text-sky-600" /> <strong>{grandTotal}</strong> inbound lead{grandTotal === 1 ? '' : 's'} this week
            <DeltaBadge delta={grandTotal - grandPreviousTotal} />
            <span className="text-slate-400">vs {grandPreviousTotal} last week</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleTogglePreview}
              disabled={previewing}
              data-testid="weekly-digest-preview-button"
              className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" /> {previewing ? 'Loading…' : previewOpen ? 'Hide preview' : 'Preview email'}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              data-testid="weekly-digest-export-button"
              className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button
              type="button"
              onClick={handleToggleLog}
              disabled={loadingLog}
              data-testid="weekly-digest-history-button"
              className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
            >
              <History className="w-3.5 h-3.5" /> {loadingLog ? 'Loading…' : logOpen ? 'Hide history' : 'History'}
            </button>
          </div>
        </div>

        {logOpen && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2" data-testid="weekly-digest-log-panel">
            {sendLog.length === 0 && <p className="text-slate-400 italic">No digest has been sent yet.</p>}
            {sendLog.map((entry, idx) => {
              const logKey = entry.sent_at || idx;
              const isExpanded = expandedLogKey === logKey;
              return (
                <div key={logKey} className="border-b border-slate-200 last:border-0 pb-2 last:pb-0">
                  <button
                    type="button"
                    onClick={() => setExpandedLogKey(isExpanded ? null : logKey)}
                    data-testid={`weekly-digest-log-entry-${idx}`}
                    className="flex flex-wrap items-center gap-2 w-full text-left"
                  >
                    {isExpanded ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                    <span className="font-medium text-slate-700">{new Date(entry.sent_at).toLocaleString('en-US', { timeZone: 'Asia/Manila' })}</span>
                    <span className={entry.sent ? 'text-emerald-600' : 'text-red-500'}>{entry.sent ? 'Sent' : 'Not sent'}</span>
                    <span className="text-slate-400">to {entry.recipients?.length || 0} recipient{entry.recipients?.length === 1 ? '' : 's'}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{(entry.region_totals || []).reduce((a, r) => a + (r.total || 0), 0)} total leads</span>
                  </button>
                  {isExpanded && (
                    <div className="mt-2 ml-5 space-y-1" data-testid={`weekly-digest-log-detail-${idx}`}>
                      <p className="text-slate-400">Recipients: {entry.recipients?.join(', ') || 'none'}</p>
                      {(entry.region_totals || [])
                        .slice()
                        .sort((a, b) => (b.total || 0) - (a.total || 0))
                        .map((r) => (
                          <div key={r.region} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorForLabel(r.region) }} />
                            <span className="text-slate-600 flex-1">{r.name}</span>
                            <span className="font-medium text-slate-700">{r.total}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {previewOpen && preview && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="weekly-digest-preview-panel">
            <p className="font-semibold text-slate-700 mb-1">Subject: {preview.subject}</p>
            <p className="text-slate-600 whitespace-pre-wrap">{preview.body}</p>
            <p className="text-slate-400 mt-2">
              Would send to: {preview.recipients.length > 0 ? preview.recipients.join(', ') : 'no Admin email configured'}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5" data-testid="weekly-digest-schedule-manager">
          <span className="text-[11px] text-slate-500 flex-shrink-0">Sends every —</span>
          <select
            value={sendWeekday}
            onChange={(e) => handleScheduleChange(Number(e.target.value), sendHour)}
            disabled={savingSchedule}
            data-testid="weekly-digest-weekday-select"
            aria-label="Weekly digest send weekday"
            className="text-[11px] border border-slate-200 rounded-full px-2 py-0.5 bg-white disabled:opacity-50"
          >
            {WEEKDAY_LABELS.map((label, idx) => (
              <option key={label} value={idx}>{label}</option>
            ))}
          </select>
          <span className="text-[11px] text-slate-500">at</span>
          <select
            value={sendHour}
            onChange={(e) => handleScheduleChange(sendWeekday, Number(e.target.value))}
            disabled={savingSchedule}
            data-testid="weekly-digest-hour-select"
            aria-label="Weekly digest send hour (Manila time)"
            className="text-[11px] border border-slate-200 rounded-full px-2 py-0.5 bg-white disabled:opacity-50"
          >
            {HOUR_OPTIONS.map((h) => (
              <option key={h} value={h}>{formatHour(h)}</option>
            ))}
          </select>
          <span className="text-[11px] text-slate-500">Manila time</span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5" data-testid="weekly-digest-cc-manager">
          <span className="text-[11px] text-slate-500 flex-shrink-0">CC on that email —</span>
          {ccEmails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 text-[11px] font-medium bg-white text-slate-600 border border-slate-200 rounded-full pl-2 pr-1 py-0.5"
              data-testid={`weekly-digest-cc-${email}`}
            >
              {email}
              <button type="button" onClick={() => handleRemoveCc(email)} disabled={savingCc} data-testid={`weekly-digest-cc-remove-${email}`} className="hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            value={ccInput}
            onChange={(e) => setCcInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCc(); } }}
            placeholder="name@example.com"
            data-testid="weekly-digest-cc-input"
            className="text-[11px] border border-slate-200 rounded-full px-2 py-0.5 w-36"
          />
          <button
            type="button"
            onClick={handleAddCc}
            disabled={savingCc || !ccInput.trim()}
            data-testid="weekly-digest-cc-add"
            className="inline-flex items-center gap-0.5 text-[11px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 hover:bg-sky-100 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>

        {sourceChartData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="weekly-inbound-source-chart">
            <p className="text-[11px] font-medium text-slate-500 mb-2">By lead source — this week vs. last week</p>
            <ResponsiveContainer width="100%" height={Math.max(90, sourceChartData.length * 40)}>
              <BarChart data={sourceChartData} layout="vertical" margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="source" width={70} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="last_week" fill="#94a3b8" name="Last week" radius={[0, 4, 4, 0]} barSize={12} />
                <Bar dataKey="this_week" name="This week" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={12}>
                  {sourceChartData.map((entry) => (
                    <Cell key={entry.source} fill={colorForLabel(entry.source)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {quietRegions.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100" data-testid="weekly-inbound-quiet-alert">
            {quietRegions.map((r) => (
              <span
                key={r.region}
                className="inline-flex items-center gap-1 text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-full pl-2 pr-1 py-0.5"
                data-testid={`weekly-inbound-quiet-${r.region}`}
                title={`Had ${r.previous_total} inbound lead(s) last week, 0 this week`}
              >
                <VolumeX className="w-3 h-3" /> {r.name} went quiet
                {r.owner_username && (() => {
                  const remainingMs = cooldownRemainingMs(r.last_nudged_at, nowTick);
                  const onCooldown = remainingMs > 0;
                  return (
                    <button
                      type="button"
                      onClick={() => handleNudgeOwner(r.region)}
                      disabled={nudgingRegion === r.region || onCooldown}
                      data-testid={`weekly-inbound-nudge-${r.region}`}
                      title={onCooldown ? `Cooldown — re-enables in ${formatCountdown(remainingMs)}` : `Email ${r.owner_name || r.owner_username}`}
                      className="ml-1 inline-flex items-center gap-0.5 text-orange-700 bg-white border border-orange-300 rounded-full px-1.5 py-0 text-[10px] hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-2.5 h-2.5" />
                      {nudgingRegion === r.region ? '…' : onCooldown ? `Cooldown ${formatCountdown(remainingMs)}` : 'Ping owner'}
                    </button>
                  );
                })()}
                {r.last_nudged_at && (
                  <span className="text-orange-400 text-[10px]" data-testid={`weekly-inbound-nudged-at-${r.region}`}>
                    (last nudged {timeAgo(r.last_nudged_at)})
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          {rows.filter((r) => r.weekly_total > 0 || r.previous_total > 0).map((r) => (
            <div key={r.region} className="flex items-center gap-3" data-testid={`weekly-inbound-row-${r.region}`}>
              <span className="flex items-center gap-1.5 text-xs text-slate-600 w-24 flex-shrink-0 truncate">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorForLabel(r.region) }} data-testid={`weekly-inbound-badge-${r.region}`} />
                {r.name}
              </span>
              <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div className="bg-sky-500 h-full rounded-full" style={{ width: `${(r.weekly_total / max) * 100}%` }} />
              </div>
              <span className="text-xs font-semibold text-slate-700 w-6 text-right flex-shrink-0">{r.weekly_total}</span>
              <DeltaBadge delta={r.weekly_total - r.previous_total} />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default WeeklyInboundDigest;
